import * as THREE from 'three';
import Application from '../Application';
/**
 * Remplace l'assise de bureau cuite de la scene par le siege baquet de la
 * scene de reference du dossier 1 (my-room-in-3d, Bruno Simon).
 *
 * Meme principe que GamerSetup : on ne peut pas remodeler un maillage cuit,
 * donc on masque le noeud nomme et on pose le nouveau modele a sa place.
 *
 * Le pied a roulettes d'origine (`chair_base`) est conserve : il appartient
 * a la scene du dossier 3, son echelle et sa position sont deja justes, et
 * le siege importe n'en a pas.
 *
 * L'ajustement est calcule a l'execution a partir de la boite englobante de
 * `chair_seat`, et non code en dur : si la scene bouge, le siege suit sans
 * qu'on ait a retoucher des valeurs magiques.
 */
export default class GamerChair {
    constructor() {
        this.chair = null;
        this.application = new Application();
        this.scene = this.application.scene;
        this.resources = this.application.resources;
        this.replaceSeat();
    }
    /** Reglages fins depuis l'URL, sans rebuild : ?chairScale=1.1&chairYaw=20 */
    params() {
        const p = new URLSearchParams(window.location.search);
        const num = (k, d) => {
            const v = p.get(k);
            const n = v === null ? NaN : parseFloat(v);
            return Number.isFinite(n) ? n : d;
        };
        return {
            scale: num('chairScale', 1),
            yaw: num('chairYaw', 0),
            lift: num('chairLift', 0),
        };
    }
    replaceSeat() {
        const oldSeat = this.scene.getObjectByName('chair_seat');
        if (!oldSeat)
            return;
        // Mesurer AVANT de masquer : une fois invisible, la boite reste
        // calculable, mais autant lire l'etat d'origine sans ambiguite.
        const target = new THREE.Box3().setFromObject(oldSeat);
        const targetSize = target.getSize(new THREE.Vector3());
        const targetCenter = target.getCenter(new THREE.Vector3());
        if (targetSize.y <= 0)
            return;
        oldSeat.visible = false;
        const model = this.resources.items.gltfModel.gamerChairModel;
        const texture = this.resources.items.texture.roomAtlasTexture;
        if (!model || !texture)
            return;
        texture.flipY = false;
        texture.colorSpace = THREE.SRGBColorSpace;
        const material = new THREE.MeshBasicMaterial({ map: texture });
        const chair = model.scene.clone(true);
        chair.traverse((child) => {
            if (child instanceof THREE.Mesh)
                child.material = material;
        });
        // Mise a l'echelle uniforme : on cale la hauteur du siege sur celle
        // de l'ancien. Un facteur par axe deformerait le baquet.
        const source = new THREE.Box3().setFromObject(chair);
        const sourceSize = source.getSize(new THREE.Vector3());
        const { scale, yaw, lift } = this.params();
        const k = (targetSize.y / sourceSize.y) * scale;
        chair.scale.setScalar(k);
        // Orientation : le siege doit regarder le bureau, comme l'ancien.
        chair.rotation.y = THREE.MathUtils.degToRad(yaw);
        // Repositionner apres mise a l'echelle et rotation, en posant la base
        // du siege a la base de l'ancien plutot qu'en alignant les centres :
        // les deux modeles n'ont pas le meme rapport dossier/assise.
        chair.updateMatrixWorld(true);
        const placed = new THREE.Box3().setFromObject(chair);
        const placedCenter = placed.getCenter(new THREE.Vector3());
        chair.position.x += targetCenter.x - placedCenter.x;
        chair.position.z += targetCenter.z - placedCenter.z;
        chair.position.y += target.min.y - placed.min.y + lift;
        chair.name = 'gamer_chair_seat';
        this.chair = chair;
        this.scene.add(chair);
    }
}
